/**
 * Example packs: a first run that is not an empty screen.
 *
 * **These are ordinary user data.** A pack installs through the same
 * repositories as anything hand-made, and produces tracks and templates that
 * are indistinguishable from them afterwards — no flag, no reserved id, no
 * protected status. Every one can be renamed, archived and deleted.
 *
 * This module is where use-case leakage would enter the codebase, so the test
 * is explicit: **nothing here may be referenced from the domain model, and the
 * installer must not know what any pack contains.** If a pack's subject matter
 * would look wrong to someone tracking guitar practice or rainfall, that is
 * fine — it is data. If the *model* would look wrong to them, something has
 * gone into the wrong layer.
 */

import type { Result } from '../model/result.js'
import { err, invalid, ok } from '../model/result.js'
import type { CheckInTemplate } from '../model/template.js'
import type { Track, TrackKind } from '../model/track.js'
import type { Storage } from '../ports/storage.js'
import type { Repositories } from './repositories.js'

export interface PackTrack {
  readonly name: string
  readonly unit?: string
  readonly description?: string
  readonly kind: TrackKind
}

export interface PackTemplate {
  readonly name: string
  readonly description?: string
  /** Positions in the pack's own track list — real ids do not exist until install. */
  readonly trackIndices: readonly number[]
}

export interface PackDefinition {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly tracks: readonly PackTrack[]
  readonly templates: readonly PackTemplate[]
}

export interface InstalledPack {
  readonly tracks: readonly Track[]
  readonly templates: readonly CheckInTemplate[]
}

/** Set once the first-run offer has been made, so it is not repeated. */
export const FIRST_RUN_KEY = 'firstRun.packsOffered'

/**
 * Installs a pack through the ordinary repositories.
 *
 * No direct storage writes and no special-casing: whatever validation and
 * numbering a hand-made track goes through, these go through too. Adding a new
 * pack is a data change, never a change here.
 */
export async function installPack(
  pack: PackDefinition,
  repos: Repositories,
): Promise<Result<InstalledPack>> {
  const created: Track[] = []

  for (const definition of pack.tracks) {
    const result = await repos.tracks.create({
      name: definition.name,
      kind: definition.kind,
      ...(definition.unit === undefined ? {} : { unit: definition.unit }),
      ...(definition.description === undefined
        ? {}
        : { description: definition.description }),
    })
    if (!result.ok) {
      return err<InstalledPack>(...result.errors)
    }
    created.push(result.value)
  }

  const templates: CheckInTemplate[] = []
  for (const definition of pack.templates) {
    const trackIds: string[] = []
    for (const index of definition.trackIndices) {
      const track = created[index]
      if (track === undefined) {
        return err<InstalledPack>(
          invalid(
            'pack.template.badIndex',
            `Pack "${pack.id}" references a track it does not define.`,
          ),
        )
      }
      trackIds.push(track.id)
    }

    const result = await repos.templates.create({
      name: definition.name,
      trackIds,
      ...(definition.description === undefined
        ? {}
        : { description: definition.description }),
    })
    if (!result.ok) {
      return err<InstalledPack>(...result.errors)
    }
    templates.push(result.value)
  }

  return ok({ tracks: created, templates })
}

/** Whether the first-run offer has already been made. */
export async function packsOffered(storage: Storage): Promise<boolean> {
  return (await storage.settings.get(FIRST_RUN_KEY)) === 'true'
}

/**
 * Records that the offer was made — including when the user declined.
 *
 * Declining must leave the app completely empty and must not be asked again.
 */
export async function markPacksOffered(storage: Storage): Promise<void> {
  await storage.settings.set(FIRST_RUN_KEY, 'true')
}

// ─────────────────────────────────────────────────────────────────────────────
// Pack definitions. Data only, from here down.
//
// Four packs across four unrelated subjects, which is the point rather than a
// convenience. One pack about food would read as the app being about food; four
// that share no vocabulary make it obvious that the subject is the user's and
// not the product's. Anybody tracking guitar practice or rainfall should look at
// this list and see four examples, not four omissions.
//
// Between them they use every shape a track can take — a numeric scale, an
// ordered word scale, an unordered set, a yes/no and a marker — so the
// possibilities are visible before someone designs their own.
// ─────────────────────────────────────────────────────────────────────────────

const ONE_TO_TEN: TrackKind = { kind: 'numeric', min: 1, max: 10, step: 1, decimals: 0 }

const MINUTES: TrackKind = { kind: 'numeric', min: 0, max: 240, step: 5, decimals: 0 }

const EATING_PACK: PackDefinition = {
  id: 'eating',
  name: 'Eating',
  description: 'How hunger and fullness move around a meal.',
  tracks: [
    { name: 'Hunger', description: 'How hungry, right now.', kind: ONE_TO_TEN },
    { name: 'Fullness', description: 'How full, right now.', kind: ONE_TO_TEN },
    {
      name: 'Food noise',
      description: 'How much food is on your mind.',
      kind: ONE_TO_TEN,
    },
    {
      name: 'Drank water',
      description: 'One tap. Nothing to rate — only that it happened, and when.',
      kind: { kind: 'marker' },
    },
  ],
  templates: [
    { name: 'Before eating', trackIndices: [0, 2] },
    { name: 'After eating', trackIndices: [1, 2] },
    { name: 'Drink', trackIndices: [3] },
  ],
}

const WORK_PACK: PackDefinition = {
  id: 'work',
  name: 'Study and work',
  description: 'What a working session was like, and where it happened.',
  tracks: [
    { name: 'Focus', description: 'How easily attention stayed put.', kind: ONE_TO_TEN },
    { name: 'Energy', description: 'How much you had to spend.', kind: ONE_TO_TEN },
    {
      name: 'Time spent',
      unit: 'min',
      description: 'How long the session ran.',
      kind: MINUTES,
    },
    {
      name: 'Where',
      description: 'An unordered set — counted and grouped, never averaged.',
      kind: {
        kind: 'enum',
        ordered: false,
        options: [
          { id: 'home', label: 'Home', score: null },
          { id: 'office', label: 'Office', score: null },
          { id: 'library', label: 'Library', score: null },
          { id: 'elsewhere', label: 'Elsewhere', score: null },
        ],
      },
    },
  ],
  templates: [
    { name: 'Starting work', trackIndices: [1, 3] },
    { name: 'Finishing work', trackIndices: [0, 2] },
  ],
}

const MOVEMENT_PACK: PackDefinition = {
  id: 'movement',
  name: 'Exercise and fitness',
  description: 'How hard it felt, how long it took, and how you felt after.',
  tracks: [
    {
      name: 'Effort',
      description: 'An ordered scale — charts and averages like a number.',
      kind: {
        kind: 'enum',
        ordered: true,
        options: [
          { id: 'easy', label: 'Easy', score: 0 },
          { id: 'steady', label: 'Steady', score: 1 },
          { id: 'hard', label: 'Hard', score: 2 },
          { id: 'allOut', label: 'All out', score: 3 },
        ],
      },
    },
    {
      name: 'Duration',
      unit: 'min',
      description: 'How long you moved for.',
      kind: MINUTES,
    },
    {
      name: 'Soreness the next day',
      description: 'Recorded the morning after, not at the time.',
      kind: ONE_TO_TEN,
    },
    {
      name: 'Moved today',
      description: 'A plain yes or no, counted as a rate over time.',
      kind: { kind: 'boolean', trueLabel: 'Yes', falseLabel: 'No' },
    },
  ],
  templates: [
    { name: 'After moving', trackIndices: [0, 1] },
    { name: 'Day check', trackIndices: [3, 2] },
  ],
}

const MIND_AND_BODY_PACK: PackDefinition = {
  id: 'mind-and-body',
  name: 'Mind and body',
  description: 'A morning and an evening reading of how things are.',
  tracks: [
    {
      name: 'Mood',
      description: 'An ordered scale, so it can be charted as a line.',
      kind: {
        kind: 'enum',
        ordered: true,
        options: [
          { id: 'low', label: 'Low', score: 0 },
          { id: 'flat', label: 'Flat', score: 1 },
          { id: 'ok', label: 'OK', score: 2 },
          { id: 'good', label: 'Good', score: 3 },
          { id: 'great', label: 'Great', score: 4 },
        ],
      },
    },
    { name: 'Sleep quality', description: 'How well you slept.', kind: ONE_TO_TEN },
    { name: 'Stress', description: 'How wound up you feel.', kind: ONE_TO_TEN },
    {
      name: 'Rested',
      description: 'Whether you woke up feeling it.',
      kind: { kind: 'boolean', trueLabel: 'Rested', falseLabel: 'Tired' },
    },
  ],
  templates: [
    { name: 'Morning', trackIndices: [3, 1, 0] },
    { name: 'Evening', trackIndices: [0, 2] },
  ],
}

export const examplePacks: readonly PackDefinition[] = [
  EATING_PACK,
  WORK_PACK,
  MOVEMENT_PACK,
  MIND_AND_BODY_PACK,
]
